import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  professionalApi,
  getProfessionalToken,
  setProfessionalToken,
  clearProfessionalToken,
} from '../services/professionalApi';
import type { Professional } from '../types/professional';

interface ProfessionalAuthContextValue {
  professional: Professional | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const ProfessionalAuthContext = createContext<ProfessionalAuthContextValue | null>(null);

export function ProfessionalAuthProvider({ children }: { children: React.ReactNode }) {
  const [professional, setProfessional] = useState<Professional | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = getProfessionalToken();
    if (!token) { setIsLoading(false); return; }
    professionalApi.getMe()
      .then(p => setProfessional(p))
      .catch(() => clearProfessionalToken())
      .finally(() => setIsLoading(false));
  }, []);

  async function login(email: string, password: string) {
    const { access_token, professional: p } = await professionalApi.login({ email, password });
    setProfessionalToken(access_token);
    setProfessional(p);
  }

  function logout() {
    clearProfessionalToken();
    setProfessional(null);
  }

  return (
    <ProfessionalAuthContext.Provider value={{
      professional,
      isAuthenticated: !!professional,
      isLoading,
      login,
      logout,
    }}>
      {children}
    </ProfessionalAuthContext.Provider>
  );
}

export function useProfessionalAuth() {
  const ctx = useContext(ProfessionalAuthContext);
  if (!ctx) throw new Error('useProfessionalAuth must be used within ProfessionalAuthProvider');
  return ctx;
}
